import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import {
  Participant,
  PDPClass,
  PDPClassServer,
  PDPProgram,
  IPDPState,
  Term,
  Topic,
} from './types';
import {
  addParticipantsRequest,
  createOrUpdateBatchPDPProgramRequest,
  createOrUpdateClassRequest,
  createOrUpdatePDPProgramRequest,
  createOrUpdateTermRequest,
  createOrUpdateTopicRequest,
  createPDPPanelRequest,
  deletePDPProgramRequest,
  getAllTermsRequest,
  getAllTopicsRequest,
  getClassRequest,
  getMyClassesRequest,
  getMyManagingClassesRequest,
  getMyManagingPDPInTermsRequest,
  getMyManagingTermsRequest,
  getMyPDPInTermsRequest,
  getMyTermsRequest,
  queryClassesRequest,
  sendMessagesToUserRequest,
  setMyGroupRequest,
  updateParticipantsRequest,
  updatePDPConfigRequest,
  exportTopicStatsRequest,
  autoGradeParticipantsRequest,
  checkClassTimeConflictsRequest,
} from '../../api/ManApi';
import { RootState, TThunkApi } from 'store/store';
import { CompanyPDP } from '../companies/companiesTypes';
import { setSingleCompanyPDPData } from '../companies/companiesSlice';

const initialState: IPDPState = {
  pdpMap: {},
};

function newTermInfo() {
  return {
    term: undefined,
    personPrograms: {},
    classes: {} as { [key: number]: PDPClass },
  };
}

function setTerm(state: IPDPState, term: Term) {
  if (!state.pdpMap[term.companyPdpId].terms)
    state.pdpMap[term.companyPdpId].terms = {};

  if (!state.pdpMap[term.companyPdpId].terms![term.id!])
    state.pdpMap[term.companyPdpId].terms![term.id!] = newTermInfo();

  state.pdpMap[term.companyPdpId!].terms![term.id!].term = term;
}

function setProgram(state: IPDPState, program: PDPProgram, pdpId: number) {
  if (!state.pdpMap[pdpId].terms) {
    state.pdpMap[pdpId].terms = {};
  }

  if (!state.pdpMap[pdpId].terms![program.termId]) {
    state.pdpMap[pdpId].terms![program.termId!] = newTermInfo();
  }

  state.pdpMap[pdpId].terms![program.termId].personPrograms[
    program.memberUserId
  ] = program;
}

function removeProgram(state: IPDPState, program: PDPProgram, pdpId: number) {
  if (!state.pdpMap[pdpId].terms) {
    state.pdpMap[pdpId].terms = {};
  }

  if (!state.pdpMap[pdpId].terms![program.termId]) {
    state.pdpMap[pdpId].terms![program.termId!] = newTermInfo();
  }

  delete state.pdpMap[pdpId].terms![program.termId].personPrograms[
    program.memberUserId
  ];
}

function setClass(state: IPDPState, clazz: PDPClassServer, pdpId: number) {
  const pdpClass = cloneDeep(clazz) as PDPClass;

  pdpClass.participants = clazz.participants.reduce(
    (current, item) => {
      current[item.userId] = item;
      return current;
    },
    {} as { [userId: number]: Participant }
  );

  if (!state.pdpMap[pdpId].classes) state.pdpMap[pdpId].classes = {};
  state.pdpMap[pdpId].classes![clazz.id] = pdpClass;

  if (!pdpClass.template) {
    if (!state.pdpMap[pdpId].terms) state.pdpMap[pdpId].terms = {};

    pdpClass.relatedTermIds.forEach((termId) => {
      if (!state.pdpMap[pdpId].terms![termId])
        state.pdpMap[pdpId].terms![termId] = newTermInfo();

      if (
        // eslint-disable-next-line no-prototype-builtins
        !state.pdpMap[pdpId].terms![termId].classes.hasOwnProperty(clazz.id)
      ) {
        state.pdpMap[pdpId].terms![termId].classes[clazz.id] = clazz;
      }
    });
  }
}

const initializeReducer = (state: any, action: any) => {
  if ('pdpId' in action.payload && !state.pdpMap[action.payload.pdpId]) {
    state.pdpMap[action.payload.pdpId] = {};
  }
};

function setAllPDPTopicHandler(
  state: IPDPState,
  action: PayloadAction<{ topics: Array<Topic>; pdpId: number }>
) {
  initializeReducer(state, action);
  if (!state.pdpMap[action.payload.pdpId].topics)
    state.pdpMap[action.payload.pdpId].topics = {};

  action.payload.topics.forEach((it) => {
    state.pdpMap[action.payload.pdpId].topics![it.id!] = it;
  });
  state.pdpMap[action.payload.pdpId].allTopicsLoaded = true;
}
function setSingleTermHandler(
  state: IPDPState,
  action: PayloadAction<{ term: Term }>
) {
  initializeReducer(state, action);
  setTerm(state, action.payload.term!);
}
function setAllPDPTermHandler(
  state: IPDPState,
  action: PayloadAction<{ terms: Array<Term>; pdpId: number }>
) {
  initializeReducer(state, action);
  action.payload.terms.forEach((it) => {
    setTerm(state, it!);
  });
}
function setAllPDPProgramHandler(
  state: IPDPState,
  action: PayloadAction<{
    programs: Array<PDPProgram>;
    termId: number;
    pdpId: number;
  }>
) {
  initializeReducer(state, action);
  action.payload.programs.forEach((it) => {
    setProgram(state, it, action.payload.pdpId);
  });
}
function deletePDPProgramHandler(
  state: IPDPState,
  action: PayloadAction<{
    program: PDPProgram;
    termId: number;
    pdpId: number;
  }>
) {
  initializeReducer(state, action);
  removeProgram(state, action.payload.program, action.payload.pdpId);
}
function setAllPDPClassesHandler(
  state: IPDPState,
  action: PayloadAction<{ classes: Array<PDPClassServer>; pdpId: number }>
) {
  initializeReducer(state, action);
  action.payload.classes.forEach((it) => {
    setClass(state, it, action.payload.pdpId);
  });
}
function setAllParticipantHandler(
  state: IPDPState,
  action: PayloadAction<{ participants: Array<Participant>; pdpId: number }>
) {
  initializeReducer(state, action);
  action.payload.participants.forEach((it) => {
    const clazz = it.pdpClassId;
    state.pdpMap[action.payload.pdpId].classes![clazz].participants[it.userId] =
      it;
  });
}
function setSingleTopicHandler(
  state: IPDPState,
  action: PayloadAction<{ topic: Topic }>
) {
  initializeReducer(state, action);
  if (!state.pdpMap[action.payload.topic.companyPDPId!].topics)
    state.pdpMap[action.payload.topic.companyPDPId!].topics = {};
  state.pdpMap[action.payload.topic.companyPDPId!].topics![
    action.payload.topic.id!
  ] = action.payload.topic;
}

export const updateParticipantsFn = async (
  { pdpId, participants }: { pdpId: number; participants: Participant[] },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return updateParticipantsRequest(store, participants)
    .then(({ data }) => {
      return Promise.resolve({ participants: data, pdpId });
    })
    .catch((e) => {
      throw new Error(e.data.message);
    });
};

const updateParticipants = createAsyncThunk(
  'pdp/updateParticipants',
  updateParticipantsFn
);

export const sendMessagesToParticipantFn = async (
  {
    message,
    clazzId,
    groups,
  }: { message: string; clazzId: number; groups: string[] },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return sendMessagesToUserRequest(store, clazzId, message, groups).then(
    ({ data }) => {
      return Promise.resolve({ result: data });
    }
  );
};

export const autoGradeParticipantsFn = async (
  {
    pdpId,
    clazzId,
    group,
    participant,
  }: {
    pdpId: number;
    clazzId: number;
    group: string | null;
    participant?: number;
  },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return autoGradeParticipantsRequest(store, clazzId, group, participant).then(
    ({ data }) => {
      return Promise.resolve({ classes: [data], pdpId: pdpId });
    }
  );
};

const sendMessagesToParticipant = createAsyncThunk(
  'pdp/sendMessagesToParticipant',
  sendMessagesToParticipantFn
);
const autoGradeParticipants = createAsyncThunk(
  'pdp/autoGradeParticipants',
  autoGradeParticipantsFn
);

export const setMyGroupFn = async (
  { pdpId, clazzId, group }: { pdpId: number; clazzId: number; group: string },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return setMyGroupRequest(store, clazzId, group).then(({ data: classes }) => {
    return Promise.resolve({ classes: [classes], pdpId });
  });
};

const setMyGroup = createAsyncThunk('pdp/setMyGroup', setMyGroupFn);

export const addParticipantsFn = async (
  {
    pdpId,
    clazzId,
    userIds,
    group,
    isCorrective,
    correctiveForClass,
  }: {
    pdpId: number;
    clazzId: number;
    userIds: Array<number>;
    group?: string;
    isCorrective: boolean;
    correctiveForClass?: number;
  },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return addParticipantsRequest(
    store,
    clazzId,
    userIds,
    group,
    isCorrective,
    correctiveForClass
  ).then(({ data: classes }) => {
    return Promise.resolve({ classes: [classes], pdpId });
  });
};

const addParticipants = createAsyncThunk(
  'pdp/addParticipants',
  addParticipantsFn
);

export const getClassFn = async (
  { classId, pdpId }: { classId: number; pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getClassRequest(store, classId).then(({ data: clazz }) => {
    return Promise.resolve({ classes: [clazz], pdpId });
  });
};

const getClass = createAsyncThunk('pdp/getClass', getClassFn);

function toServerClass(clazz: PDPClass) {
  const pdpClass = cloneDeep(clazz) as PDPClassServer;
  pdpClass.participants = [];
  return pdpClass;
}

export const createOrUpdateClassFn = async (
  {
    clazz,
    pdpId,
    skipConflictCheck,
  }: { clazz: PDPClass; pdpId: number; skipConflictCheck?: boolean },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  const pdpClass = toServerClass(clazz);
  return createOrUpdateClassRequest(store, pdpClass, skipConflictCheck).then(
    ({ data: clazz }) => {
      return Promise.resolve({ classes: [clazz], pdpId });
    },
    (e) => {
      return ThunkAPI.rejectWithValue(e.response) as any;
    }
  );
};

const createOrUpdateClass = createAsyncThunk(
  'pdp/createOrUpdateClass',
  createOrUpdateClassFn
);

export const createOrUpdateBatchPDPProgramFn = async (
  { programs, pdpId }: { programs: Array<PDPProgram>; pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return createOrUpdateBatchPDPProgramRequest(store, programs).then(
    ({ data: pdpPrograms }) => {
      return Promise.resolve({
        programs: pdpPrograms,
        termId: pdpPrograms[0].termId as number,
        pdpId,
      });
    }
  );
};

const createOrUpdateBatchPDPProgram = createAsyncThunk(
  'pdp/createOrUpdateBatchPDPProgram',
  createOrUpdateBatchPDPProgramFn
);

export const createOrUpdatePDPProgramFn = async (
  { program, pdpId }: { program: PDPProgram; pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return createOrUpdatePDPProgramRequest(store, program).then(
    ({ data: pdpProgram }) => {
      return Promise.resolve({
        programs: [pdpProgram],
        termId: pdpProgram.termId,
        pdpId,
      });
    }
  );
};

export const deletePDPProgramFn = async (
  { program, pdpId }: { program: PDPProgram; pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return deletePDPProgramRequest(store, program.id).then(
    ({ data: pdpProgram }) => {
      return Promise.resolve({
        program: program,
        termId: pdpProgram.termId,
        pdpId,
      });
    }
  );
};

const createOrUpdatePDPProgram = createAsyncThunk(
  'pdp/createOrUpdatePDPProgram',
  createOrUpdatePDPProgramFn
);

const deletePDPProgram = createAsyncThunk(
  'pdp/deletePDPProgram',
  deletePDPProgramFn
);

export const getMyManagingPDPInTermFn = async (
  { termId, pdpId }: { termId: number; pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getMyManagingPDPInTermsRequest(store, termId).then(
    ({ data: programs }) => {
      return Promise.resolve({
        programs,
        termId,
        pdpId,
      });
    }
  );
};

const getMyManagingPDPInTerm = createAsyncThunk(
  'pdp/getMyManagingPDPInTerm',
  getMyManagingPDPInTermFn
);

export const getMyPDPInTermFn = async (
  { termId, pdpId }: { termId: number; pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getMyPDPInTermsRequest(store, termId).then(({ data: pdpProgram }) => {
    return Promise.resolve({
      programs: [pdpProgram],
      termId,
      pdpId,
    });
  });
};

const getMyPDPInTerm = createAsyncThunk('pdp/getMyPDPInTerm', getMyPDPInTermFn);

export const getMyManagingClassesFn = async (
  {
    pdpId,
    startDate,
    endDate,
    termId,
  }: { pdpId: number; startDate?: number; endDate?: number; termId?: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getMyManagingClassesRequest(
    store,
    pdpId,
    startDate,
    endDate,
    termId
  ).then(({ data: classes }) => {
    return Promise.resolve({
      classes,
      pdpId,
    });
  });
};

const getMyManagingClasses = createAsyncThunk(
  'pdp/getMyManagingClasses',
  getMyManagingClassesFn
);

export const getMyManagingTermsFn = async (
  { pdpId }: { pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getMyManagingTermsRequest(store, pdpId).then(({ data: terms }) => {
    return Promise.resolve({
      terms,
      pdpId,
    });
  });
};

const getMyManagingTerms = createAsyncThunk(
  'pdp/getMyManagingTerms',
  getMyManagingTermsFn
);

export const getMyClassesFn = async (
  {
    pdpId,
    startDate,
    endDate,
    termId,
  }: { pdpId: number; startDate?: number; endDate?: number; termId?: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getMyClassesRequest(store, pdpId, startDate, endDate, termId).then(
    ({ data: classes }) => {
      return Promise.resolve({
        classes,
        pdpId,
      });
    }
  );
};

const getMyClasses = createAsyncThunk('pdp/getMyClasses', getMyClassesFn);

export const getMyTermsFn = async (
  { pdpId }: { pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getMyTermsRequest(store, pdpId).then(({ data: terms }) => {
    return Promise.resolve({
      terms,
      pdpId,
    });
  });
};

const getMyTerms = createAsyncThunk('pdp/getMyTerms', getMyTermsFn);

export const queryClassesFn = async (
  {
    pdpId,
    participantid,
    startDate,
    endDate,
    topicId,
    includeDeleted = false,
    isTemplate = false,
    termId,
  }: {
    pdpId: number;
    participantid?: number;
    startDate?: number;
    endDate?: number;
    topicId?: number;
    includeDeleted?: boolean;
    isTemplate?: boolean;
    termId?: number;
  },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return queryClassesRequest(
    store,
    pdpId,
    startDate,
    endDate,
    participantid,
    topicId,
    includeDeleted,
    isTemplate,
    termId
  ).then(({ data: classes }) => {
    return Promise.resolve({
      classes,
      pdpId,
    });
  });
};

const queryClasses = createAsyncThunk('pdp/queryClasses', queryClassesFn);

export const getAllTermsFn = async (
  { pdpId, includeDeleted }: { pdpId: number; includeDeleted: boolean },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getAllTermsRequest(store, pdpId, includeDeleted).then(
    ({ data: terms }) => {
      return Promise.resolve({ terms, pdpId });
    }
  );
};

const getAllTerms = createAsyncThunk('pdp/getAllTerms', getAllTermsFn);

export const createOrUpdateTermFn = async (
  { term }: { term: Term },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return createOrUpdateTermRequest(store, term).then(({ data: term }) => {
    return Promise.resolve({ term });
  });
};

const createOrUpdateTerm = createAsyncThunk(
  'pdp/createOrUpdateTerm',
  createOrUpdateTermFn
);

export const getAllTopicsFn = async (
  { pdpId }: { pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return getAllTopicsRequest(store, pdpId).then(({ data: topics }) => {
    ThunkAPI.dispatch(setAllPDPTopic({ topics: topics, pdpId: pdpId }));
    return Promise.resolve({ topics, pdpId });
  });
};

const getAllTopics = createAsyncThunk('pdp/getAllTopics', getAllTopicsFn);

export const createOrUpdateTopicFn = async (
  { topic }: { topic: Topic },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return createOrUpdateTopicRequest(store, topic).then(({ data: topic }) => {
    return Promise.resolve({ topic });
  });
};
const createOrUpdateTopic = createAsyncThunk(
  'pdp/createOrUpdateTopic',
  createOrUpdateTopicFn
);

export const exportTopicStatsFn = async (
  {
    termIds,
  }: {
    termIds: number[];
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return exportTopicStatsRequest(store, termIds)
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const fileNameHeader = 'x-suggested-filename';
      const suggestedFileName = response.headers[fileNameHeader];
      const effectiveFileName =
        suggestedFileName === undefined
          ? 'export.xlsx'
          : decodeURIComponent(suggestedFileName);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', effectiveFileName); //or any other extension
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const exportTopicStats = createAsyncThunk(
  'pdp/exportTopicStats',
  exportTopicStatsFn
);

export const createPDPPanelFn = async (
  { companyId, templateName }: { companyId: number; templateName: string },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return createPDPPanelRequest(store, companyId, templateName).then(
    ({ data: pdpConfig }) => {
      ThunkAPI.dispatch(setSingleCompanyPDPData({ pdp: pdpConfig }));
      return Promise.resolve({ pdpConfig });
    }
  );
};

const createPDPPanel = createAsyncThunk('pdp/createPDPPanel', createPDPPanelFn);

export const updatePDPConfigFn = async (
  { pdpConfig }: { pdpConfig: CompanyPDP },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return updatePDPConfigRequest(store, pdpConfig).then(
    ({ data: pdpConfig }) => {
      ThunkAPI.dispatch(setSingleCompanyPDPData({ pdp: pdpConfig }));
      return Promise.resolve({ pdpConfig });
    }
  );
};

const updatePDPConfig = createAsyncThunk(
  'pdp/updatePDPConfig',
  updatePDPConfigFn
);

export const checkClassTimeConflictsFn = async (
  { clazz, pdpId }: { clazz: PDPClass; pdpId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  const pdpClass = toServerClass(clazz);
  return checkClassTimeConflictsRequest(store, pdpClass).then(({ data }) => {
    return Promise.resolve({ response: data, pdpId });
  });
};

const checkClassTimeConflicts = createAsyncThunk(
  'pdp/checkClassTimeConflicts',
  checkClassTimeConflictsFn
);

const pdpSlice = createSlice({
  name: 'pdp',
  initialState: initialState,
  reducers: {
    setAllPDPTopic: setAllPDPTopicHandler,
    setSingleTerm: setSingleTermHandler,
    setAllPDPTerm: setAllPDPTermHandler,
    setAllPDPProgram: setAllPDPProgramHandler,
    setAllPDPClasses: setAllPDPClassesHandler,
    setAllParticipant: setAllParticipantHandler,
    setSingleTopic: setSingleTopicHandler,
  },
  extraReducers: (builder) => {
    builder.addCase(updateParticipants.fulfilled, (state, action) => {
      setAllParticipantHandler(state, action);
    });

    builder.addCase(addParticipants.fulfilled, (state, action) => {
      setAllPDPClassesHandler(state, action);
    });
    builder.addCase(setMyGroup.fulfilled, (state, action) => {
      setAllPDPClassesHandler(state, action);
    });

    builder.addCase(getClass.fulfilled, (state, action) => {
      setAllPDPClassesHandler(state, action);
    });

    builder.addCase(createOrUpdateClass.fulfilled, (state, action) => {
      setAllPDPClassesHandler(state, action);
    });

    builder.addCase(autoGradeParticipants.fulfilled, (state, action) => {
      setAllPDPClassesHandler(state, action);
    });

    builder.addCase(
      createOrUpdateBatchPDPProgram.fulfilled,
      (state, action) => {
        setAllPDPProgramHandler(state, action);
      }
    );

    builder.addCase(createOrUpdatePDPProgram.fulfilled, (state, action) => {
      setAllPDPProgramHandler(state, action);
    });
    builder.addCase(deletePDPProgram.fulfilled, (state, action) => {
      deletePDPProgramHandler(state, action);
    });

    builder.addCase(getMyManagingPDPInTerm.fulfilled, (state, action) => {
      setAllPDPProgramHandler(state, action);
    });

    builder.addCase(getMyPDPInTerm.fulfilled, (state, action) => {
      setAllPDPProgramHandler(state, action);
    });

    builder.addCase(getMyManagingClasses.fulfilled, (state, action) => {
      setAllPDPClassesHandler(state, action);
    });

    builder.addCase(getMyManagingTerms.fulfilled, (state, action) => {
      setAllPDPTermHandler(state, action);
    });

    builder.addCase(getMyClasses.fulfilled, (state, action) => {
      setAllPDPClassesHandler(state, action);
    });

    builder.addCase(getMyTerms.fulfilled, (state, action) => {
      setAllPDPTermHandler(state, action);
    });

    builder.addCase(queryClasses.fulfilled, (state, action) => {
      setAllPDPClassesHandler(state, action);
    });

    builder.addCase(getAllTerms.fulfilled, (state, action) => {
      setAllPDPTermHandler(state, action);
    });

    builder.addCase(createOrUpdateTerm.fulfilled, (state, action) => {
      setSingleTermHandler(state, action);
    });

    builder.addCase(getAllTopics.fulfilled, (state, action) => {
      setAllPDPTopicHandler(state, action);
    });

    builder.addCase(createOrUpdateTopic.fulfilled, (state, action) => {
      setSingleTopicHandler(state, action);
    });

    // TODO: remove dispatching after migration  of companySlice
    builder.addCase(createPDPPanel.fulfilled, () => {
      // console.log('done');
    });

    builder.addCase(updatePDPConfig.fulfilled, () => {
      // console.log('done');
    });
  },
});

export const {
  setAllPDPTopic,
  setSingleTerm,
  setAllPDPTerm,
  setAllPDPProgram,
  setAllPDPClasses,
  setAllParticipant,
  setSingleTopic,
} = pdpSlice.actions;

export {
  updateParticipants,
  addParticipants,
  setMyGroup,
  getClass,
  createOrUpdateClass,
  createOrUpdateBatchPDPProgram,
  createOrUpdatePDPProgram,
  deletePDPProgram,
  getMyManagingPDPInTerm,
  getMyPDPInTerm,
  getMyManagingClasses,
  getMyManagingTerms,
  getMyClasses,
  getMyTerms,
  queryClasses,
  getAllTerms,
  createOrUpdateTerm,
  getAllTopics,
  createOrUpdateTopic,
  sendMessagesToParticipant,
  autoGradeParticipants,
  createPDPPanel,
  updatePDPConfig,
  exportTopicStats,
  checkClassTimeConflicts,
};

export default pdpSlice.reducer;
